import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Star, Flame, Trophy, Camera, Upload, Medal, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format rank with ordinal suffix
const formatRank = (rank) => {
  if (!rank) return 'N/A';
  const num = parseInt(rank);
  
  // Handle teens (11, 12, 13) which all end with 'th'
  if (num >= 11 && num <= 13) {
    return `${num}th`;
  }
  
  // Get the last digit to determine ordinal suffix
  const lastDigit = num % 10;
  
  switch (lastDigit) {
    case 1: return `${num}st`;
    case 2: return `${num}nd`;
    case 3: return `${num}rd`;
    default: return `${num}th`;
  }
};

// Helper function to get rank styling based on rank number
const getRankStyling = (rank) => {
  if (!rank) return { color: '#7C3AED', icon: Trophy, size: 'w-5 h-5' };
  
  const num = parseInt(rank);
  if (num === 1) return { color: '#FFD700', icon: Trophy, size: 'w-6 h-6' }; // Gold
  if (num === 2) return { color: '#C0C0C0', icon: Medal, size: 'w-6 h-6' }; // Silver  
  if (num === 3) return { color: '#CD7F32', icon: Award, size: 'w-6 h-6' }; // Bronze
  return { color: '#7C3AED', icon: Trophy, size: 'w-5 h-5' }; // Purple for others
};

export default function Children() {
  const { getAuthHeaders } = useAuth();
  const [children, setChildren] = useState([]);
  const [childRanks, setChildRanks] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: 'Muslim',
    age: 8,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const fetchChildren = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/children`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setChildren(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch children:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  // Fetch dense ranks for all children
  useEffect(() => {
    const fetchChildRanks = async () => {
      if (children.length === 0) return;
      
      try {
        const rankPromises = children.map(async (child) => {
          try {
            const response = await fetch(`${API_URL}/api/leaderboard/dense-rank/child/${child.child_id}`);
            if (response.ok) {
              const rankData = await response.json();
              return { child_id: child.child_id, dense_rank: rankData.dense_rank };
            } else {
              console.warn(`Failed to fetch rank for child ${child.child_id}: ${response.status}`);
              return { child_id: child.child_id, dense_rank: null };
            }
          } catch (error) {
            console.warn(`Error fetching rank for child ${child.child_id}:`, error);
            return { child_id: child.child_id, dense_rank: null };
          }
        });
        
        const rankResults = await Promise.all(rankPromises);
        const rankMap = {};
        rankResults.forEach(({ child_id, dense_rank }) => {
          rankMap[child_id] = dense_rank;
        });
        
        setChildRanks(rankMap);
      } catch (error) {
        console.error('Failed to fetch child ranks:', error);
      }
    };

    fetchChildRanks();
  }, [children]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image must be less than 2MB');
        return;
      }

      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadProfileImage = async (childId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/children/${childId}/profile-image`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Authorization': getAuthHeaders()['Authorization'],
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload image');
    }

    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const url = editingChild 
        ? `${API_URL}/api/children/${editingChild.child_id}`
        : `${API_URL}/api/children`;
      
      const response = await fetch(url, {
        method: editingChild ? 'PUT' : 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const savedChild = await response.json();
        const childId = savedChild.child_id;

        if (selectedFile) {
          await uploadProfileImage(childId, selectedFile);
        }

        toast.success(editingChild ? 'Child updated!' : 'Child added!');
        setDialogOpen(false);
        setEditingChild(null);
        setFormData({ first_name: '', last_name: '', gender: 'Muslim', age: 8 });
        setSelectedFile(null);
        setImagePreview(null);
        fetchChildren();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save child');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save child');
    }
  };

  const handleDeleteImage = async (childId) => {
    try {
      const response = await fetch(`${API_URL}/api/children/${childId}/profile-image`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        toast.success('Profile image removed');
        fetchChildren();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove image');
      }
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const openEditDialog = (child) => {
    setEditingChild(child);
    setFormData({
      first_name: child.first_name,
      last_name: child.last_name,
      gender: child.gender,
      age: child.age,
    });
    setImagePreview(child.profile_image || null);
    setSelectedFile(null);
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingChild(null);
    setFormData({ first_name: '', last_name: '', gender: 'Muslim', age: 8, profile_image: '' });
    setImagePreview(null);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#264653] font-outfit">Children</h1>
            <p className="text-[#6B7280] mt-1">Manage your children's profiles</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openAddDialog}
                className="bg-[#08513F] hover:bg-[#064031] text-white rounded-full px-6"
                data-testid="add-child-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Child
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-3xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#264653] font-outfit">
                  {editingChild ? 'Edit Child' : 'Add New Child'}
                </DialogTitle>
                <DialogDescription>
                  {editingChild ? 'Update your child\'s information' : 'Add a new child to your family'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Photo Upload */}
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <Avatar className="h-24 w-24 border-4 border-[#D4AF37]/30">
                      <AvatarImage src={imagePreview} />
                      <AvatarFallback className="bg-[#08513F] text-white text-2xl">
                        {formData.first_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-0 right-0 w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center text-white hover:bg-[#B5952F] transition-colors"
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-sm"
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Upload Photo
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="Ahmed"
                      className="rounded-xl h-12"
                      data-testid="child-first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Ali"
                      className="rounded-xl h-12"
                      data-testid="child-last-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) => setFormData({ ...formData, gender: value })}
                  >
                    <SelectTrigger className="rounded-xl h-12" data-testid="child-gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Muslim">Muslim (Boy)</SelectItem>
                      <SelectItem value="Muslimah">Muslimah (Girl)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="age">Age ({formData.age} years)</Label>
                  <Input
                    id="age"
                    type="range"
                    min="5"
                    max="15"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) })}
                    className="w-full"
                    data-testid="child-age-input"
                  />
                  <div className="flex justify-between text-sm text-[#6B7280]">
                    <span>5</span>
                    <span>15</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#E76F51] hover:bg-[#D65A3C] text-white rounded-xl h-12 mt-6"
                  data-testid="save-child-btn"
                >
                  {editingChild ? 'Update Child' : 'Add Child'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Children Grid */}
        {children.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <div className="w-20 h-20 bg-[#08513F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Plus className="w-10 h-10 text-[#08513F]" />
            </div>
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">No Children Yet</h2>
            <p className="text-[#6B7280] mb-6">Add your first child to get started with the challenge!</p>
            <Button
              onClick={openAddDialog}
              className="bg-[#08513F] hover:bg-[#064031] text-white rounded-full px-8"
              data-testid="empty-add-child-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Child
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map((child, index) => (
              <Card 
                key={child.child_id}
                className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg card-hover animate-slide-up overflow-hidden"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`child-card-${child.child_id}`}
              >
                {/* Card Header with Color */}
                <div className={`h-24 ${child.gender === 'Muslim' ? 'bg-islamic-green' : 'bg-gradient-to-r from-[#E76F51] to-[#F4A261]'}`}>
                  <div className="flex justify-end p-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(child)}
                      className="bg-white/20 hover:bg-white/30 text-white rounded-full"
                      data-testid={`edit-child-${child.child_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <CardContent className="pt-0 -mt-12">
                  <div className="flex flex-col items-center">
                    <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                      <AvatarImage src={child.profile_image} />
                      <AvatarFallback className={`text-2xl font-bold ${
                        child.gender === 'Muslim' 
                          ? 'bg-[#08513F] text-white' 
                          : 'bg-[#E76F51] text-white'
                      }`}>
                        {child.first_name?.charAt(0)}{child.last_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>

                    <h3 className="text-xl font-bold text-[#264653] font-outfit mt-4">
                      {child.first_name} {child.last_name}
                    </h3>
                    <p className="text-sm text-[#6B7280]">
                      {child.gender} • Age {child.age}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 mt-6">
                    <div className="text-center p-4 bg-[#FFFBEB] rounded-2xl">
                      <Star className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                      <p className="text-4xl font-bold text-[#264653] font-outfit">{child.total_points}</p>
                      <p className="text-xs text-[#6B7280]">Points</p>
                    </div>
                    <div className="text-center p-4 bg-[#FFFBEB] rounded-2xl">
                      <Flame className="w-6 h-6 text-[#E76F51] mx-auto mb-2" />
                      <p className="text-4xl font-bold text-[#264653] font-outfit">{child.longest_streak}</p>
                      <p className="text-xs text-[#6B7280]">Best Streak</p>
                    </div>
                    <div className="text-center p-4 bg-[#FFFBEB] rounded-2xl">
                      {(() => {
                        const rankStyling = getRankStyling(childRanks[child.child_id]);
                        return (
                          <div className="flex flex-col items-center gap-1">
                            <rankStyling.icon className={`${rankStyling.size} text-[${rankStyling.color}]`} />
                            <p className="text-4xl font-bold font-outfit" style={{ color: rankStyling.color }}>
                              {formatRank(childRanks[child.child_id])}
                            </p>
                            <p className="text-xs text-[#6B7280]">Rank</p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
